import { useMemo } from 'react'
import type { Api } from '@reduxjs/toolkit/query/react'
import type { LibConfig, LocalConfig } from '../types/lib-config'
import { EntityStatus } from '../store/model/entityStatus.model'
import {
  AgentWidgetMode,
  isAgentWidgetMode,
  normalizeAgentWidgetMode,
} from '../types/agent-mode'

export type AgentFullConfig = Partial<LocalConfig> & {
  id?: string
  isAiEnabled?: boolean
  status?: EntityStatus
  mode?: AgentWidgetMode
}

export type HookReturn = {
  agent: AgentFullConfig
}

// RTKQ Api type is generic; we just need "something that behaves like an Api"
type AnyApi = Api<any, any, any, any, any>

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0

const pickOverrides = (config: LibConfig): Partial<LocalConfig> => {
  const c = config as any
  const out: Partial<LocalConfig> = {}

  // only copy defined, non-empty strings so we don't overwrite API with blanks
  if (isNonEmptyString(c.name)) out.name = c.name
  if (isAgentWidgetMode(c.mode)) out.mode = normalizeAgentWidgetMode(c.mode)
  if (isNonEmptyString(c.searchPlaceholder)) out.searchPlaceholder = c.searchPlaceholder
  if (isNonEmptyString(c.companyName)) out.companyName = c.companyName
  if (isNonEmptyString(c.companyIconUrl)) out.companyIconUrl = c.companyIconUrl
  if (isNonEmptyString(c.customColor)) out.customColor = c.customColor
  if (isNonEmptyString(c.description)) out.description = c.description
  if (isNonEmptyString(c.phoneNumber)) out.phoneNumber = c.phoneNumber
  if (isNonEmptyString(c.docUrl)) out.docUrl = c.docUrl
  if (isNonEmptyString(c.apiDocUrl)) out.apiDocUrl = c.apiDocUrl
  if (isNonEmptyString(c.email)) out.email = c.email
  if (isNonEmptyString(c.schedule)) out.schedule = c.schedule
  if (typeof c.address === 'object' && c.address) out.address = c.address
  if (typeof c.showMap === 'boolean') out.showMap = c.showMap
  if (isNonEmptyString(c.googleMapApiKey)) out.googleMapApiKey = c.googleMapApiKey
  if (typeof c.support === 'object' && c.support) out.support = c.support
  if (isNonEmptyString(c.greeting)) out.greeting = c.greeting
  if (Array.isArray(c.customOptions)) out.customOptions = c.customOptions

  return out
}

export const useGetAgentConfig = (config: LibConfig, agentApi: AnyApi): HookReturn => {
  const { useGetOneAgentQuery } = agentApi as any

  const { data: agentData, isError, isLoading } = useGetOneAgentQuery(
    { id: (config as any).agentId || '' },
    { skip: !(config as any).agentId }
  )

  const agent = useMemo<AgentFullConfig>(() => {
    const overrides = pickOverrides(config)

    // ✅ Remote agent mode
    if ((config as any).agentId) {
      if (isError) {
        console.warn(
          `There was an error getting information from your agent. You may need to modify your restrictions to accept requests from this domain.`
        )
      }

      if (!agentData || isLoading) {
        return {
          ...(config as any),
          ...overrides,
          isAiEnabled: false,
          status: EntityStatus.draft,
        }
      }

      // --- Normalize support ---
      const rawSupportType = agentData.supportType
      const rawSupportContact = agentData.supportContact

      let support: AgentFullConfig['support'] | undefined = undefined

      if (
        (rawSupportType === 'email' || rawSupportType === 'page') &&
        isNonEmptyString(rawSupportContact)
      ) {
        support = { type: rawSupportType, value: rawSupportContact.trim() }
      }

      // --- Normalize customOptions (only keep valid ones) ---
      const customOptions =
        agentData.customOptions
          ?.filter(
            (opt: { icon?: string; label?: string; link?: string }) =>
              isNonEmptyString(opt.icon) && isNonEmptyString(opt.label) && isNonEmptyString(opt.link)
          )
          .map(({ icon, label, link }: { icon: string; label: string; link: string }) => ({
            icon,
            label,
            link,
            openNewTab: false,
          })) || []

      const remote: AgentFullConfig = {
        id: agentData.id,
        name: agentData.name,
        mode: normalizeAgentWidgetMode(agentData.type, 'chat'),
        searchPlaceholder: isNonEmptyString(agentData.searchPlaceholder)
          ? agentData.searchPlaceholder
          : undefined,
        companyName: agentData.companyName,
        companyIconUrl: agentData.companyIconMedia?.media?.sourceUrl,
        customColor: agentData.customHexColor, // ✅ API default
        description: agentData.description,
        phoneNumber: agentData.phoneNumber,
        docUrl: agentData.docUrl,
        apiDocUrl: agentData.apiDocUrl,
        email: agentData.email,
        schedule: agentData.schedule,
        address: agentData.address,
        showMap: agentData.showMap,
        googleMapApiKey: agentData.googleMapApiKey,
        status: agentData.status,
        isAiEnabled: true,
        support,
        greeting: agentData.greeting,
        customOptions,
      }

      // ✅ IMPORTANT: overrides win (config > API)
      return {
        ...remote,
        ...overrides,
        // keep these authoritative from API unless you explicitly want overrides:
        id: remote.id,
        status: remote.status,
        isAiEnabled: remote.isAiEnabled,
      }
    }

    // ✅ Local (no remote agentId) mode
    return {
      ...(config as any),
      mode: normalizeAgentWidgetMode((config as any).mode, 'chat'),
      ...overrides,
      isAiEnabled: !!config.mobileMcpToolBridge,
      status: EntityStatus.live,
    }
  }, [agentData, isError, isLoading, config])

  return { agent }
}
